import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import {
  Drawer,
  IconButton,
  AppBar,
  Toolbar,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import CustomDrawer from './CustomDrawer';
import CoreTabNavigator from './CoreTabNavigator';
import { isAuthenticated } from '../../helpers/userHelpers';
import { getLocationOn, Colors } from '../../helpers/navigationHelpers';
import { useDrawer } from '../../contexts/DrawerContext';

// Import placeholder components
import Notifications from '../../pages/home/notifications/Notifications';
import BottomTabAIChat from '../../pages/home/ai-chat/BottomTabAIChat';
import ReelsFeed from '../../pages/home/reels-feed/ReelsFeed';
import TopTabNavigator from './TopTabNavigator';
import SearchScreen from '../../pages/home/search/SearchScreen';
import Location from '../../pages/home/location/Location';
import Profile from '../../pages/home/profile/Profile';
import CreateReel from '../../pages/home/create-reel/CreateReel';

const DRAWER_WIDTH = 280;

const DrawerNavigator = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [locationOn, setLocationOn] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isOpen, openDrawer, closeDrawer } = useDrawer();

  useEffect(() => {
    // Check authentication
    if (!isAuthenticated()) {
      navigate('/landing');
      return;
    }

    // Update locationOn state
    setLocationOn(getLocationOn());

    // Listen for storage changes
    const handleStorageChange = () => {
      setLocationOn(getLocationOn());
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [navigate]);

  // Sync context state with local state (only when context changes externally)
  useEffect(() => {
    if (isOpen !== mobileOpen) {
      setMobileOpen(isOpen);
    }
  }, [isOpen]);

  const handleDrawerToggle = () => {
    const newState = !mobileOpen;
    setMobileOpen(newState);
    if (newState) {
      openDrawer();
    } else {
      closeDrawer();
    }
  };

  const handleDrawerClose = () => {
    setMobileOpen(false);
    closeDrawer();
  };

  const primaryColor = locationOn ? Colors.CUSTOM_YELLOW : Colors.PRIMARY;

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* App Bar - Only show on mobile/tablet when drawer is hidden */}
      {/* {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            backgroundColor: primaryColor,
            zIndex: theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div">
              Tellmiz
            </Typography>
          </Toolbar>
        </AppBar>
      )} */}

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
        >
          <CustomDrawer onClose={handleDrawerClose} />
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
          open
        >
          <CustomDrawer />
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          height: '100vh',
          overflow: 'hidden',
          marginTop: { xs: '64px', md: 0 }, // Account for AppBar on mobile
        }}
      >
        <Routes>
          {/* Default route redirects to feed */}
          <Route path="/" element={<Navigate to="feed" replace />} />
          
          {/* Core Tab Navigator routes */}
          <Route
            path="/feed"
            element={
              <CoreTabNavigator>
                <TopTabNavigator />
              </CoreTabNavigator>
            }
          />
          <Route
            path="/feed/*"
            element={
              <CoreTabNavigator>
                <TopTabNavigator />
              </CoreTabNavigator>
            }
          />
          <Route
            path="/search"
            element={
              <CoreTabNavigator>
                <SearchScreen />
              </CoreTabNavigator>
            }
          />
          <Route
            path="/create-reel"
            element={
              <CoreTabNavigator>
                <CreateReel />
              </CoreTabNavigator>
            }
          />
          <Route
            path="/location"
            element={
              <CoreTabNavigator>
                <Location />
              </CoreTabNavigator>
            }
          />
          <Route
            path="/profile"
            element={
              <CoreTabNavigator>
                <Profile />
              </CoreTabNavigator>
            }
          />

          {/* Drawer routes (without bottom tabs) */}
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/ai-chat" element={<BottomTabAIChat />} />
          <Route path="/reels-feed" element={<ReelsFeed />} />
        </Routes>
      </Box>
    </Box>
  );
};

export default DrawerNavigator;

